import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Trophy,
  Medal,
  Award,
  Users,
  Heart,
  Star,
  Crown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/public/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Failed to load leaderboard:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "R";
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-muted-foreground font-bold">{rank}</span>;
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return "bg-yellow-500 text-white";
    if (rank === 2) return "bg-gray-400 text-white";
    if (rank === 3) return "bg-amber-600 text-white";
    return "bg-muted text-muted-foreground";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Top Responders
            </span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
            VALORLink™ Heroes
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Recognizing our dedicated peer responders who go above and beyond to support veterans in their time of need.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <Card className="card-valor">
            <CardContent className="p-6 text-center">
              <Users className="w-10 h-10 text-primary mx-auto mb-2" />
              <p className="text-3xl font-bold text-foreground">{leaderboard?.total_responders || 0}</p>
              <p className="text-sm text-muted-foreground">Total Responders</p>
            </CardContent>
          </Card>
          <Card className="card-valor">
            <CardContent className="p-6 text-center">
              <Heart className="w-10 h-10 text-secondary mx-auto mb-2" />
              <p className="text-3xl font-bold text-foreground">{leaderboard?.active_responders || 0}</p>
              <p className="text-sm text-muted-foreground">Veterans Helped</p>
            </CardContent>
          </Card>
        </div>

        {/* Leaderboard */}
        <Card className="card-valor">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Crown className="w-5 h-5 text-yellow-500" />
              Leaderboard
            </CardTitle>
            <CardDescription>
              Responders ranked by number of veterans supported
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!leaderboard?.leaderboard || leaderboard.leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No responders on the leaderboard yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Be the first to help a veteran and earn your spot!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Top 3 Podium */}
                {leaderboard.leaderboard.length >= 3 && (
                  <div className="flex justify-center items-end gap-4 mb-8 py-4">
                    {/* 2nd Place */}
                    <div className="text-center">
                      <Avatar className="w-16 h-16 mx-auto border-4 border-gray-300">
                        <AvatarImage src={leaderboard.leaderboard[1]?.picture} />
                        <AvatarFallback className="bg-gray-400 text-white text-lg">
                          {getInitials(leaderboard.leaderboard[1]?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="mt-2">
                        <Medal className="w-6 h-6 text-gray-400 mx-auto" />
                        <p className="font-semibold text-sm truncate max-w-[100px]">
                          {leaderboard.leaderboard[1]?.name?.split(" ")[0]}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {leaderboard.leaderboard[1]?.veterans_helped} helped
                        </p>
                      </div>
                    </div>
                    
                    {/* 1st Place */}
                    <div className="text-center -mt-4">
                      <div className="relative">
                        <Avatar className="w-20 h-20 mx-auto border-4 border-yellow-400">
                          <AvatarImage src={leaderboard.leaderboard[0]?.picture} />
                          <AvatarFallback className="bg-yellow-500 text-white text-xl">
                            {getInitials(leaderboard.leaderboard[0]?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <Crown className="w-6 h-6 text-yellow-500 absolute -top-2 left-1/2 -translate-x-1/2" />
                      </div>
                      <div className="mt-2">
                        <Trophy className="w-6 h-6 text-yellow-500 mx-auto" />
                        <p className="font-bold truncate max-w-[120px]">
                          {leaderboard.leaderboard[0]?.name?.split(" ")[0]}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {leaderboard.leaderboard[0]?.veterans_helped} helped
                        </p>
                      </div>
                    </div>
                    
                    {/* 3rd Place */}
                    <div className="text-center">
                      <Avatar className="w-16 h-16 mx-auto border-4 border-amber-500">
                        <AvatarImage src={leaderboard.leaderboard[2]?.picture} />
                        <AvatarFallback className="bg-amber-600 text-white text-lg">
                          {getInitials(leaderboard.leaderboard[2]?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="mt-2">
                        <Medal className="w-6 h-6 text-amber-600 mx-auto" />
                        <p className="font-semibold text-sm truncate max-w-[100px]">
                          {leaderboard.leaderboard[2]?.name?.split(" ")[0]}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {leaderboard.leaderboard[2]?.veterans_helped} helped
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Full List */}
                {leaderboard.leaderboard.map((responder, index) => (
                  <div 
                    key={responder.user_id}
                    className={`flex items-center gap-4 p-4 rounded-lg transition-colors ${
                      index < 3 ? "bg-muted/50" : "hover:bg-muted/30"
                    }`}
                  >
                    <div className="w-10 flex justify-center">
                      {getRankIcon(responder.rank)}
                    </div>
                    
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={responder.picture} />
                      <AvatarFallback className={`${index < 3 ? getRankBadge(responder.rank) : "bg-primary text-white"}`}>
                        {getInitials(responder.name)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground truncate">{responder.name}</p>
                        {responder.applicant_type === "civilian" && (
                          <Badge variant="outline" className="text-xs">Civilian</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {responder.background}
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xl font-bold text-primary">{responder.veterans_helped}</p>
                      <p className="text-xs text-muted-foreground">veterans</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Call to Action */}
        <Card className="card-valor mt-8 bg-gradient-to-r from-primary to-primary/80 text-white">
          <CardContent className="p-8 text-center">
            <Star className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
              Want to Make a Difference?
            </h3>
            <p className="text-white/80 mb-4">
              Join our team of volunteer responders and help veterans in your community.
            </p>
            <Link to="/">
              <Button variant="secondary" className="bg-white text-primary hover:bg-white/90">
                Become a Responder
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Leaderboard;
